import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { alpha } from '@mui/material/styles';
import {
  Timestamp,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import LeadCompactCard from './LeadCompactCard';

type DirectoryUser = { id: string; name?: string | null; email?: string | null; roleId?: string | null; role?: string | null };
type LeadStatusDef = { id: string; label?: string | null; name?: string | null; order?: number; color?: string | null };
type DateBasis = 'created' | 'assigned';
type DateRangeKey = 'today' | '7d' | 'month' | '30d';
type ViewMode = 'snapshot' | 'compare';

const RANGE_OPTIONS: Array<{ key: DateRangeKey; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: 'month', label: 'This Month' },
  { key: '30d', label: 'Last 30 Days' },
];

const COMPARE_BATCH_SIZE = 6;

function rangeBounds(range: DateRangeKey): { start: Date; end: Date } {
  const now = new Date();
  const end = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  if (range === 'today') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { start, end };
  }
  if (range === '7d') {
    return { start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), end };
  }
  if (range === 'month') {
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end };
  }
  return { start: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), end };
}

function normalizeRoleKey(u: DirectoryUser): string {
  return String(u.roleId || u.role || '').trim().toLowerCase();
}

const SALES_ROLE_KEYS = new Set(['sales-manager', 'sales-team', 'sales']);

// Shared by both Snapshot and Compare: fetch a person's leads within a date range, by either
// creation date or assignment date. `assignedAt` is stamped on every lead doc at assignment
// time (creation or reassignment) — see AddLeadDrawer.tsx and LeadDetailDrawer.tsx.
async function loadLeadsForRange(selectedEmail: string, basis: DateBasis, start: Date, end: Date): Promise<any[]> {
  const dateField = basis === 'created' ? 'createdAt' : 'assignedAt';
  const snap = await getDocs(
    query(
      collection(db, 'leads'),
      where('assignedTo', '==', selectedEmail),
      where(dateField, '>=', Timestamp.fromDate(start)),
      where(dateField, '<', Timestamp.fromDate(end))
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function monthKeyOffset(monthKey: string, offset: number): string {
  const [y, m] = monthKey.split('-').map((n) => Number(n));
  const d = new Date(y, (m - 1) + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

type MonthStatusRow = { key: string; label: string; leadsByStatus: Record<string, any[]>; total: number };

async function loadMonthStatusRow(monthKey: string, selectedEmail: string, basis: DateBasis): Promise<MonthStatusRow> {
  const [yearStr, monthStr] = monthKey.split('-');
  const year = Number(yearStr);
  const monthIdx = Number(monthStr) - 1;
  const start = new Date(year, monthIdx, 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(year, monthIdx + 1, 1);
  end.setHours(0, 0, 0, 0);

  const leads = await loadLeadsForRange(selectedEmail, basis, start, end);
  const leadsByStatus: Record<string, any[]> = {};
  for (const l of leads) {
    const key = String(l?.status || '').trim().toLowerCase() || 'unknown';
    (leadsByStatus[key] = leadsByStatus[key] || []).push(l);
  }
  const label = start.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  return { key: monthKey, label, leadsByStatus, total: leads.length };
}

export default function LeadAssignmentStatusPanel(props: {
  roleId: string | null;
  currentUserEmail: string;
  userDirectory: DirectoryUser[];
  leadStatuses: LeadStatusDef[];
  onOpenLead: (lead: any) => void;
  detailLeadId?: string | null;
}) {
  const { roleId, currentUserEmail, userDirectory, leadStatuses, onOpenLead, detailLeadId } = props;

  const normalizedRole = String(roleId || '').trim().toLowerCase();
  const isAdmin = normalizedRole === 'super-admin' || normalizedRole === 'admin';
  const isManager = normalizedRole === 'sales-manager';
  const selfEmail = String(currentUserEmail || '').trim().toLowerCase();

  const userOptions = useMemo(() => {
    const list = userDirectory.filter((u) => String(u.email || '').trim());
    if (isAdmin) return list;
    if (isManager) {
      return list.filter(
        (u) => SALES_ROLE_KEYS.has(normalizeRoleKey(u)) || String(u.email || '').trim().toLowerCase() === selfEmail
      );
    }
    return list.filter((u) => String(u.email || '').trim().toLowerCase() === selfEmail);
  }, [userDirectory, isAdmin, isManager, selfEmail]);

  const [selectedEmail, setSelectedEmail] = useState<string>('');
  useEffect(() => {
    if (selectedEmail && userOptions.some((u) => String(u.email).trim().toLowerCase() === selectedEmail)) return;
    const preferSelf = userOptions.find((u) => String(u.email || '').trim().toLowerCase() === selfEmail);
    const fallback = preferSelf || userOptions[0];
    setSelectedEmail(fallback ? String(fallback.email).trim().toLowerCase() : '');
  }, [userOptions, selfEmail, selectedEmail]);

  const [viewMode, setViewMode] = useState<ViewMode>('snapshot');
  const [basis, setBasis] = useState<DateBasis>('created');
  const [range, setRange] = useState<DateRangeKey>('today');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<any[]>([]);
  const [expandedStatusKey, setExpandedStatusKey] = useState<string | null>(null);

  const statusLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of leadStatuses) {
      const label = String(s?.label || s?.name || s?.id || '').trim();
      if (!label) continue;
      m.set(String(s.id).toLowerCase(), label);
      m.set(label.toLowerCase(), label);
    }
    return m;
  }, [leadStatuses]);

  const statusOrder = useMemo(
    () =>
      leadStatuses
        .map((s, idx) => {
          const label = String(s?.label || s?.name || s?.id || '').trim();
          const key = String(s?.id || label).trim().toLowerCase();
          return { key, label, order: typeof s?.order === 'number' ? s.order : idx + 1 };
        })
        .filter((s) => s.key && s.label)
        .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label)),
    [leadStatuses]
  );

  const statusLabelFor = useCallback(
    (key: string) => statusOrder.find((s) => s.key === key)?.label || statusLabelMap.get(key) || key || 'Unknown',
    [statusOrder, statusLabelMap]
  );

  // ---------- Snapshot ----------
  useEffect(() => {
    if (viewMode !== 'snapshot') return;
    if (!selectedEmail) {
      setLeads([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExpandedStatusKey(null);

    (async () => {
      try {
        const { start, end } = rangeBounds(range);
        const rows = await loadLeadsForRange(selectedEmail, basis, start, end);
        if (!cancelled) setLeads(rows);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load leads.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewMode, selectedEmail, basis, range]);

  const statusCounts = useMemo(() => {
    const grouped = new Map<string, any[]>();
    for (const l of leads) {
      const key = String(l?.status || '').trim().toLowerCase() || 'unknown';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(l);
    }
    const known = statusOrder
      .filter((s) => grouped.has(s.key))
      .map((s) => ({ key: s.key, label: s.label, leads: grouped.get(s.key) || [] }));
    const knownKeys = new Set(known.map((s) => s.key));
    const extras = Array.from(grouped.entries())
      .filter(([key]) => !knownKeys.has(key))
      .map(([key, rows]) => ({ key, label: statusLabelFor(key), leads: rows }));
    return [...known, ...extras];
  }, [leads, statusOrder, statusLabelFor]);

  const selectedUserLabel = useMemo(() => {
    const u = userOptions.find((x) => String(x.email || '').trim().toLowerCase() === selectedEmail);
    return u?.name || u?.email || selectedEmail || '—';
  }, [userOptions, selectedEmail]);

  const expandedRows = expandedStatusKey ? statusCounts.find((s) => s.key === expandedStatusKey)?.leads || [] : [];

  // ---------- Compare ----------
  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const [compareRows, setCompareRows] = useState<MonthStatusRow[]>([]);
  const [compareMonthsLoaded, setCompareMonthsLoaded] = useState(0);
  const [compareInitialLoading, setCompareInitialLoading] = useState(false);
  const [compareLoadingMore, setCompareLoadingMore] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [expandedCompareCell, setExpandedCompareCell] = useState<{ monthKey: string; statusKey: string } | null>(null);

  const loadMoreCompareMonths = useCallback(async () => {
    if (!selectedEmail) return;
    const isInitial = compareMonthsLoaded === 0;
    if (isInitial) setCompareInitialLoading(true);
    else setCompareLoadingMore(true);
    setCompareError(null);
    try {
      const nextKeys: string[] = [];
      for (let i = 0; i < COMPARE_BATCH_SIZE; i += 1) {
        nextKeys.push(monthKeyOffset(currentMonthKey, -(compareMonthsLoaded + i)));
      }
      const rows = await Promise.all(nextKeys.map((mk) => loadMonthStatusRow(mk, selectedEmail, basis)));
      setCompareRows((prev) => [...prev, ...rows]);
      setCompareMonthsLoaded((prev) => prev + COMPARE_BATCH_SIZE);
    } catch (err: any) {
      setCompareError(err?.message || 'Failed to load comparison data.');
    } finally {
      setCompareInitialLoading(false);
      setCompareLoadingMore(false);
    }
  }, [selectedEmail, basis, compareMonthsLoaded, currentMonthKey]);

  // Selecting a different user or basis invalidates everything already loaded.
  useEffect(() => {
    setCompareRows([]);
    setCompareMonthsLoaded(0);
    setExpandedCompareCell(null);
  }, [selectedEmail, basis]);

  useEffect(() => {
    if (viewMode !== 'compare') return;
    if (compareRows.length > 0 || compareInitialLoading) return;
    loadMoreCompareMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, compareRows.length, compareInitialLoading]);

  const compareColumns = useMemo(() => {
    const seen = new Set<string>();
    const known = statusOrder.filter((s) => {
      const has = compareRows.some((r) => (r.leadsByStatus[s.key] || []).length > 0);
      if (has) seen.add(s.key);
      return has;
    });
    const extras: Array<{ key: string; label: string }> = [];
    compareRows.forEach((r) => {
      Object.keys(r.leadsByStatus).forEach((key) => {
        if (seen.has(key)) return;
        seen.add(key);
        extras.push({ key, label: statusLabelFor(key) });
      });
    });
    return [...known, ...extras];
  }, [compareRows, statusOrder, statusLabelFor]);

  const expandedCompareLeads = expandedCompareCell
    ? compareRows.find((r) => r.key === expandedCompareCell.monthKey)?.leadsByStatus[expandedCompareCell.statusKey] || []
    : [];

  return (
    <Paper sx={{ p: 0, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 0 }}
        sx={{ px: 1.5, py: 1.25 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupsOutlinedIcon fontSize="small" color="secondary" />
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Lead Assignment Status</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {viewMode === 'compare' ? 'Month-over-month status trend, per person' : 'Assigned leads by current status, per person'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant={viewMode === 'snapshot' ? 'contained' : 'outlined'} onClick={() => setViewMode('snapshot')}>
            Snapshot
          </Button>
          <Button size="small" variant={viewMode === 'compare' ? 'contained' : 'outlined'} onClick={() => setViewMode('compare')}>
            Compare
          </Button>
        </Stack>
      </Stack>
      <Divider />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ px: 1.5, py: 1.25 }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={selectedEmail}
            onChange={(e) => setSelectedEmail(String(e.target.value))}
            displayEmpty
          >
            {userOptions.length === 0 && (
              <MenuItem value="" disabled>
                No users
              </MenuItem>
            )}
            {userOptions.map((u) => {
              const email = String(u.email || '').trim().toLowerCase();
              return (
                <MenuItem key={u.id} value={email}>
                  {u.name || email}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={basis} onChange={(e) => setBasis(e.target.value as DateBasis)}>
            <MenuItem value="created">Created Date</MenuItem>
            <MenuItem value="assigned">Assigned Date</MenuItem>
          </Select>
        </FormControl>

        {viewMode === 'snapshot' && (
          <>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {RANGE_OPTIONS.map((opt) => (
                <Chip
                  key={opt.key}
                  size="small"
                  label={opt.label}
                  color={range === opt.key ? 'primary' : 'default'}
                  variant={range === opt.key ? 'filled' : 'outlined'}
                  onClick={() => setRange(opt.key)}
                />
              ))}
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" variant="outlined" label={`${leads.length} leads`} sx={{ fontWeight: 700 }} />
          </>
        )}
      </Stack>
      <Divider />

      {viewMode === 'compare' ? (
        <Box sx={{ px: 1.5, py: 1.25 }}>
          {compareError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {compareError}
            </Alert>
          )}
          {compareInitialLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading comparison…</Typography>
            </Stack>
          ) : compareRows.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}>
              No leads assigned to {selectedUserLabel} in any loaded month.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box
                sx={{
                  minWidth: 120 + (compareColumns.length + 1) * 110,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'visible',
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `120px repeat(${compareColumns.length + 1}, minmax(90px, 1fr))`,
                    px: 1.25,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                      position: 'sticky',
                      left: 0,
                      zIndex: 6,
                      bgcolor: 'background.paper',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                      pl: 0.25,
                    }}
                  >
                    Month
                  </Typography>
                  {compareColumns.map((c) => (
                    <Typography key={c.key} sx={{ fontSize: 12, fontWeight: 900, color: 'text.secondary', textAlign: 'center' }} noWrap>
                      {c.label}
                    </Typography>
                  ))}
                  <Typography sx={{ fontSize: 12, fontWeight: 900, color: 'text.secondary', textAlign: 'center' }}>Total</Typography>
                </Box>

                {compareRows.map((r) => (
                  <React.Fragment key={r.key}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `120px repeat(${compareColumns.length + 1}, minmax(90px, 1fr))`,
                        px: 1.25,
                        py: 0.75,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 800,
                          position: 'sticky',
                          left: 0,
                          zIndex: 5,
                          bgcolor: 'background.paper',
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                          pl: 0.25,
                        }}
                      >
                        {r.label}
                      </Typography>
                      {compareColumns.map((c) => {
                        const count = (r.leadsByStatus[c.key] || []).length;
                        const active = expandedCompareCell?.monthKey === r.key && expandedCompareCell?.statusKey === c.key;
                        if (count <= 0) {
                          return (
                            <Typography key={c.key} sx={{ fontSize: 12.5, opacity: 0.7, textAlign: 'center' }}>
                              {count}
                            </Typography>
                          );
                        }
                        return (
                          <ButtonBase
                            key={c.key}
                            onClick={() =>
                              setExpandedCompareCell((prev) =>
                                prev && prev.monthKey === r.key && prev.statusKey === c.key ? null : { monthKey: r.key, statusKey: c.key }
                              )
                            }
                            sx={{
                              justifyContent: 'center',
                              minHeight: 26,
                              borderRadius: 1,
                              px: 0.5,
                              py: 0.35,
                              ...(active ? { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.1) } : {}),
                              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) },
                            }}
                          >
                            <Typography sx={{ fontSize: 12.5, fontWeight: active ? 900 : 700, color: 'primary.main', textAlign: 'center' }}>
                              {count}
                            </Typography>
                          </ButtonBase>
                        );
                      })}
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{r.total}</Typography>
                    </Box>

                    <Collapse in={expandedCompareCell?.monthKey === r.key} timeout={200} unmountOnExit>
                      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03), px: 1.25, py: 1 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 900 }}>
                            {expandedCompareCell ? statusLabelFor(expandedCompareCell.statusKey) : ''} — {r.label} ({expandedCompareLeads.length})
                          </Typography>
                          <Button size="small" onClick={() => setExpandedCompareCell(null)}>
                            Close
                          </Button>
                        </Stack>
                        {expandedCompareLeads.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No leads to show.
                          </Typography>
                        ) : (
                          <Stack spacing={0}>
                            {expandedCompareLeads.map((l: any) => (
                              <LeadCompactCard
                                key={l.id}
                                lead={l}
                                selected={detailLeadId === l.id}
                                onClick={() => onOpenLead(l)}
                                statusLabel={statusLabelFor(String(l?.status || '').trim().toLowerCase())}
                                assignedLabel={selectedUserLabel}
                                badge={null}
                                view="sheet"
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </React.Fragment>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
                <Button size="small" variant="outlined" disabled={compareLoadingMore} onClick={() => loadMoreCompareMonths()}>
                  {compareLoadingMore ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  ) : (
                    `Load ${COMPARE_BATCH_SIZE} more months`
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ px: 1.5, py: 1.25 }}>
          {error && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading…</Typography>
            </Stack>
          ) : statusCounts.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}>
              No leads assigned to {selectedUserLabel} in this range.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {statusCounts.map((s) => (
                <Chip
                  key={s.key}
                  label={`${s.label} (${s.leads.length})`}
                  color={expandedStatusKey === s.key ? 'primary' : 'default'}
                  variant={expandedStatusKey === s.key ? 'filled' : 'outlined'}
                  onClick={() => setExpandedStatusKey((prev) => (prev === s.key ? null : s.key))}
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>
          )}

          <Collapse in={Boolean(expandedStatusKey)} timeout="auto" unmountOnExit>
            <Box
              sx={{
                mt: 1.25,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.25, py: 1, bgcolor: 'grey.50' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, flex: 1 }} noWrap>
                  {expandedStatusKey ? statusLabelFor(expandedStatusKey) : ''} — {selectedUserLabel} ({expandedRows.length})
                </Typography>
              </Stack>
              <Divider />
              <Stack spacing={0} sx={{ p: 1 }}>
                {expandedRows.map((l: any) => (
                  <LeadCompactCard
                    key={l.id}
                    lead={l}
                    selected={detailLeadId === l.id}
                    onClick={() => onOpenLead(l)}
                    statusLabel={statusLabelFor(String(l?.status || '').trim().toLowerCase())}
                    assignedLabel={selectedUserLabel}
                    badge={null}
                    view="sheet"
                  />
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
}
